import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

/**
 * Realtime channel registry. Tracks every Supabase realtime channel the app
 * subscribes to, plus the factory function that created it. On iOS the
 * WebView is suspended in the background — the WebSocket appears open after
 * resume but channels are dead. `recreateAllChannels` (called from
 * `realtimeReconnect.ts` on appStateChange) tears down every channel and
 * runs the factories again so subscriptions actually fire on fresh joins.
 *
 * Consumers register a stable string key per channel; calling
 * `registerChannel` with an existing key replaces the prior channel.
 */

export type ChannelFactory = () => RealtimeChannel;

interface Entry {
  factory: ChannelFactory;
  channel: RealtimeChannel;
}

const entries = new Map<string, Entry>();

export function registerChannel(key: string, factory: ChannelFactory): RealtimeChannel {
  const existing = entries.get(key);
  if (existing) supabase.removeChannel(existing.channel);
  const channel = factory();
  entries.set(key, { factory, channel });
  return channel;
}

export function unregisterChannel(key: string): void {
  const e = entries.get(key);
  if (!e) return;
  supabase.removeChannel(e.channel);
  entries.delete(key);
}

export async function recreateAllChannels(): Promise<void> {
  const factories = Array.from(entries.entries()).map(([k, e]) => [k, e.factory] as const);
  for (const [, e] of entries) supabase.removeChannel(e.channel);
  entries.clear();
  for (const [key, factory] of factories) {
    try {
      entries.set(key, { factory, channel: factory() });
    } catch (err) {
      console.warn(`[realtime] factory failed for ${key}`, err);
    }
  }
}
